import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  FileText, Search, Filter, ChevronRight, Calendar,
  AlertCircle, CheckCircle, Clock, XCircle, Download
} from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'

const API_URL = process.env.REACT_APP_BACKEND_URL

const STATUT_CONFIG = {
  declare:               { label: 'Déclaré',           color: 'bg-slate-100 text-slate-700',   dot: 'bg-slate-400' },
  en_instruction:        { label: 'En instruction',   color: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-500' },
  en_attente_compagnie:  { label: 'Attente compagnie',color: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-500' },
  expertise_en_cours:    { label: 'Expertise',        color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
  negociation:           { label: 'Négociation',      color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500' },
  cloture_favorable:     { label: 'Clôturé ✓',        color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  cloture_refus:         { label: 'Refusé',           color: 'bg-red-100 text-red-700',       dot: 'bg-red-500' },
  sans_suite:            { label: 'Sans suite',       color: 'bg-slate-100 text-slate-500',   dot: 'bg-slate-300' },
}

// Référence affichée côté DAF : flotte → N° client (ou N° dossier Cie si mise en cause) ; autres parcours → N° Pilote
export function referenceDaf(s) {
  const flotte = s.type_sinistre === 'flotte' || s.polices?.type_police === 'flotte'
  if (!flotte) return s.numero_sinistre
  if (s.est_mise_en_cause && s.numero_dossier_compagnie) return s.numero_dossier_compagnie
  return s.numero_sinistre_client || s.numero_dossier_compagnie || s.numero_sinistre
}

export default function DAFSinistres() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [sinistres, setSinistres] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterStatut, setFilterStatut] = useState('all')
  const [filterType, setFilterType] = useState('all')

  useEffect(() => {
    if (profile?.tenant_id) fetchSinistres()
  }, [profile])

  async function fetchSinistres() {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/api/sinistres?tenant_id=${profile.tenant_id}&user_id=${profile.id}&role=${profile.role}`)
      const data = await res.json()
      setSinistres(data.sinistres || [])
    } catch (err) {
      console.error('Error fetching sinistres:', err)
    }
    setLoading(false)
  }

  function formatDate(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  function formatCurrency(amount) {
    if (amount == null) return '—'
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Apply filters
  const filteredSinistres = sinistres.filter(s => {
    if (filterStatut !== 'all' && s.statut !== filterStatut) return false
    if (filterType !== 'all' && s.type_sinistre !== filterType) return false
    if (search) {
      const searchLower = search.toLowerCase()
      return (
        s.numero_sinistre?.toLowerCase().includes(searchLower) ||
        s.nature_sinistre?.toLowerCase().includes(searchLower) ||
        s.declarant?.prenom?.toLowerCase().includes(searchLower) ||
        s.declarant?.nom?.toLowerCase().includes(searchLower)
      )
    }
    return true
  })

  const openCount = sinistres.filter(s => !s.date_cloture).length
  const closedCount = sinistres.filter(s => s.date_cloture).length
  const totalProvisions = sinistres.reduce((sum, s) => sum + (Number(s.provision_compagnie) || 0), 0)

  return (
    <div className="p-8" data-testid="daf-sinistres">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Sinistres
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {filteredSinistres.length} dossier{filteredSinistres.length > 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total', value: sinistres.length, color: 'bg-slate-100 text-slate-700' },
          { label: 'En cours', value: openCount, color: 'bg-blue-100 text-blue-700' },
          { label: 'Clôturés', value: closedCount, color: 'bg-emerald-100 text-emerald-700' },
          { label: 'Provisions compagnie', value: formatCurrency(totalProvisions), color: 'bg-amber-100 text-amber-700' },
        ].map(({ label, value, color }) => (
          <div key={label} className={`rounded-lg px-4 py-3 ${color}`}>
            <p className="text-xl font-bold tabular-nums" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {value}
            </p>
            <p className="text-xs font-medium mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher par numéro, nature, déclarant..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-9 pr-4 rounded-lg border border-slate-200 text-sm outline-none
              focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>

        {/* Status filter */}
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white outline-none
            focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">Tous les statuts</option>
          <option value="declare">Déclaré</option>
          <option value="en_instruction">En instruction</option>
          <option value="en_attente_compagnie">Attente compagnie</option>
          <option value="expertise_en_cours">Expertise</option>
          <option value="negociation">Négociation</option>
          <option value="cloture_favorable">Clôturé favorable</option>
          <option value="cloture_refus">Refusé</option>
        </select>

        {/* Type filter */}
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white outline-none
            focus:ring-2 focus:ring-emerald-500"
        >
          <option value="all">Tous les types</option>
          <option value="dab">Dommages aux biens</option>
          <option value="flotte">Flotte automobile</option>
          <option value="rc_tiers">Responsabilité civile</option>
          <option value="amiable">Amiable</option>
          <option value="contentieux">Contentieux</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Chargement...</p>
        </div>
      ) : filteredSinistres.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <FileText size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Aucun sinistre trouvé</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Référence
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Nature
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Date
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Police
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Provision
                </th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  Statut
                </th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredSinistres.map((s) => {
                const cfg = STATUT_CONFIG[s.statut] || STATUT_CONFIG.declare
                
                return (
                  <tr 
                    key={s.id}
                    onClick={() => navigate(`/daf/sinistre/${s.id}`)}
                    className="border-b border-slate-100 hover:bg-emerald-50/50 cursor-pointer transition-colors"
                    data-testid="sinistre-row"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold text-emerald-600" data-testid="daf-sinistre-reference">
                          {referenceDaf(s)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-800 truncate max-w-[200px]">
                        {s.nature_sinistre || s.type_sinistre}
                      </p>
                      {s.est_amiable && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-1 py-0.5 rounded">
                          Amiable
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-600">{formatDate(s.date_fait)}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-slate-600">
                        {s.polices?.libelle || s.polices?.type_police}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-mono text-slate-800">
                        {formatCurrency(s.provision_compagnie)}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${cfg.dot}`} />
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight size={16} className="text-slate-300" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
